import { Hourglass } from "lucide-react";
import { useSelector } from "react-redux";

import { IgAccordion } from "@intelligems/ig-theme/IgAccordion";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types/functions/analytics/analyze-resource-tinybird/analyze-resource-tinybird";
import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { getDaysFromHistory } from "@intelligems/ig-analytics/date";
import {
  camelToSnakeCase,
  ensureNumber,
  officialCategoryName,
} from "@intelligems/ig-utils/common";
import { Stack } from "@mui/material";
import { filterDashboardDataTb } from "@src/api/analytics/dashboard-tinybird";
import { useGetTinybirdExperienceAnalysis } from "@src/api/analytics/get-tinybird-experience-analysis";
import { DashboardPages } from "@src/api/types/dashboards";
import { useAnalyticsSummary } from "@src/features/ai/components/usages/useAnalyticsSummary";
import { CheckoutBlockViewedNotAvailable } from "@src/features/analytics/components/CheckoutBlockViewedNotAvailable";
import { useIsCheckoutBlockViewedDataIncomplete } from "@src/features/analytics/hooks/use-checkout-block-viewed-available";
import { useSetExperienceAnalyticsFilters } from "@src/features/analytics/hooks/use-set-experience-analytics-filters";
import {
  getExperienceTotal,
  hasFiltersBeenModified,
} from "@src/features/analytics/utils";
import { MobileEmptyState } from "@src/features/mobileAnalytics/shared/components/MobileEmptyState";
import { useGenerateGraphConfig } from "@src/features/mobileAnalytics/testDetails/hooks/useGenerateGraphConfig";
import { useReadOnlyMode } from "@src/features/publicShareLinks/hooks/useReadOnlyMode";
import { useViewAnalyticsPage } from "@src/hooks/mixpanel";
import { Sparkle } from "@src/images/Icons/Sparkle";
import { getExperienceAnalyticsFilters } from "@src/store/slices/dashboardFilters";
import { getPrimaryMetricId } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { selectedVariations } from "@src/store/slices/variations";
import { UseQueryResult } from "@tanstack/react-query";

import { MobileAnalyticsAISummary } from "./components/MobileAnalyticsAISummary";
import { KeyMetrics } from "./sections/KeyMetrics";
import { MobileAudienceTable } from "./sections/MobileAudienceTable";
import { MobileStatisticalSignificance } from "./sections/MobileStatisticalSignificance";
import { MobileTimeSeries } from "./sections/MobileTimeSeries";
import { OverviewMetrics } from "./sections/OverviewMetrics";

interface AnalyticsDashboardProps {
  experience: ExperienceFe;
}

export const MobileAnalyticsDashboard = ({
  experience,
}: AnalyticsDashboardProps) => {
  useViewAnalyticsPage(
    `${officialCategoryName(experience.category, false)}_${camelToSnakeCase(DashboardPages[0])}`
  );

  const { isReadOnly } = useReadOnlyMode();
  const isCheckoutBlockViewedIncomplete =
    useIsCheckoutBlockViewedDataIncomplete();
  const variations = useSelector(selectedVariations);
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);
  const primaryMetricId = useSelector(getPrimaryMetricId);

  const generatedOverviewGraphConfig = useGenerateGraphConfig();
  const variationOverviewData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[] =
    useGetTinybirdExperienceAnalysis(
      {
        graphConfigs: generatedOverviewGraphConfig,
        experience,
        overviewDataLoaded: true,
      },
      "experiences"
    );

  const experienceTotals = {
    orders: getExperienceTotal(variationOverviewData, "n_orders"),
    visitors: getExperienceTotal(variationOverviewData, "n_visitors"),
    revenue: getExperienceTotal(variationOverviewData, "net_revenue"),
  };

  useSetExperienceAnalyticsFilters(
    experience,
    variations,
    primaryMetricId,
    filterDashboardDataTb(
      variationOverviewData,
      generatedOverviewGraphConfig[0]?.datasetId || "variation_overview",
      generatedOverviewGraphConfig[0] || {}
    )
  );

  const diff = getDaysFromHistory(
    experience.experienceActions || [],
    ensureNumber(dashboardFilters.endTime) ??
      ensureNumber(dashboardFilters.hiddenEndTime)
  );

  const isLoading = variationOverviewData.some(
    (vod) => vod.isLoading || vod.isPending
  );

  const noData =
    isLoading ||
    !variationOverviewData ||
    !variationOverviewData[0] ||
    !variationOverviewData[0].data ||
    (variationOverviewData[0].isSuccess &&
      (variationOverviewData[0].data?.metrics?.length || 0) < 1 &&
      !hasFiltersBeenModified(dashboardFilters));

  const graphConfigs = generatedOverviewGraphConfig;
  const aiMetrics = Array.from(
    new Set(generatedOverviewGraphConfig.flatMap((g) => g.metrics))
  );

  // Start fetching AI summary immediately on page load
  const summaryData = useAnalyticsSummary({
    experience,
    dashboardData: variationOverviewData,
  });

  if (!experience) {
    return null;
  }

  if (experience.status === "pending") {
    return (
      <MobileEmptyState
        icon={Hourglass}
        title="Test not started"
        description="You'll need to start your test before we can show you any data. Please visit Intelligems on your desktop to start your test."
      />
    );
  }

  if (noData && !isLoading) {
    // The upsell-collection-gap warning is the accurate explanation here, so it
    // takes precedence over the generic "No data yet" empty state.
    if (isCheckoutBlockViewedIncomplete) {
      return (
        <Stack sx={{ width: "100%" }}>
          <CheckoutBlockViewedNotAvailable />
        </Stack>
      );
    }
    return (
      <MobileEmptyState
        icon={Hourglass}
        title="No data yet"
        description="You'll need a few more visitors before we can show you any data. Please check back later."
      />
    );
  }
  return (
    <Stack
      // Used for the Chameleon tour to allow the tour
      // to start when there is actual data to present.
      data-analytics-has-data={(!noData).toString()}
      id={"dashboard-page-template"}
      spacing={4}
      sx={{ p: 4, width: "100%" }}
    >
      <CheckoutBlockViewedNotAvailable />

      <OverviewMetrics
        experienceTotals={experienceTotals}
        isLoading={isLoading}
      />

      {/* AI summary - hidden on share links (read-only mode) */}
      {!isReadOnly && (
        <IgAccordion
          sx={{ paddingY: 2, "::before": { display: "none" } }} // the ::before element is to remove the default MUI Accordion border
          title={
            <Stack direction="row" spacing={4} alignItems="center">
              <Sparkle variant={"default"} />
              <IgTypography variant="h3" fontWeight={600}>
                AI summary
              </IgTypography>
            </Stack>
          }
          defaultExpanded={false}
        >
          <MobileAnalyticsAISummary summaryData={summaryData} />
        </IgAccordion>
      )}
      <KeyMetrics
        data={variationOverviewData}
        testDuration={diff}
        totalExperienceVisitors={experienceTotals.visitors || 0}
        graphConfigs={graphConfigs}
        compareVariationId={dashboardFilters.compareGroupB || ""}
      />

      <MobileStatisticalSignificance
        variationOverviewData={variationOverviewData}
        graphConfigs={graphConfigs}
      />

      <MobileTimeSeries
        variationOverviewData={variationOverviewData}
        graphConfigs={graphConfigs}
        overviewDataLoaded={variationOverviewData[0]?.isSuccess || false}
      />

      <MobileAudienceTable
        experience={experience}
        variations={variations}
        dashboardData={variationOverviewData}
        graphConfigs={graphConfigs}
        testDuration={diff}
      />
    </Stack>
  );
};
